/**
 * 新着通知ポップアップの純粋ロジック (副作用なし)。
 *
 * - detectNewNotifications: 既知 (seen) でない notifId の通知だけを抽出する新着判定。
 * - formatNotificationLine / buildPopupItems: ポップアップに出す文言・クリック遷移先の整形。
 * - notificationTargetPath: 通知クリック時の遷移先 (QA-093 系)。解決は共通 resolver
 *   (`notificationDestination`) に委譲し、NotificationDrawer と同一の契約を共有する。
 * - decidePopupActions: visible/hidden × popup/sound 設定から toast 表示・音再生を判定 (通知改善 B)。
 *
 * これらは React / DOM / localStorage に一切依存しないため、そのまま unit test できる。
 * ポーリングや表示 (副作用) は useNotificationPopup フック側が担う。
 */
import type { Notification } from '@/types';
import { notificationDestination } from '@/utils/notificationDestination';
import type { Locale } from '@/i18n';
import { notificationsJa } from '@/i18n/locales/ja/notifications';
import { translate } from '@/i18n/translate';
// 名前空間 `notifications` を登録する (ポップアップの文言を引く)
import '@/i18n/namespaces/notifications';

/** ポップアップを個別トーストで出す最大件数。これを超えたら 1 件に集約する。 */
export const MAX_INDIVIDUAL_POPUPS = 3;

/**
 * グループが伸びたときに **同じ notifId でも再ポップアップする**種別
 * (notification-message-receive-grouping)。
 *
 * BE は短時間の同種イベントを 1 レコードへ畳み込むため、2 通目以降は notifId が
 * 変わらない。書類アップロードでは steering 486 がこの挙動を許容したが、
 * **顧客からの新着メッセージが無音になるのは業務影響が別物**なので、メッセージ系だけ
 * 「グループの版 (count / lastAt)」を新着判定に織り込む。
 *
 * **ここに載っていない種別は従来どおり notifId 単位**。既存 upload group が伸びても
 * 2 件目以降で toast / 音が再発しないことを構造的に保証する。
 */
const GROUP_REVISION_POPUP_TYPES: ReadonlySet<string> = new Set(['portal.message_received']);

/** 新着判定に使う seen キーの入力。`Notification` の必要な field だけを要求する。 */
export interface NotificationSeenKeyInput {
  notifId: string;
  type?: string | null;
  count?: number | null;
  lastAt?: string | null;
}

/**
 * 新着判定に使う「見たことがある」キーを返す。
 *
 * - allowlist 外の種別・集約属性を持たない通知 → **`notifId` そのもの** (従来と完全に同じ)
 * - allowlist 内で集約属性を持つ通知 → `notifId#<count>@<lastAt>` の版つきキー
 *
 * 版つきキーにすることで、(a) グループが伸びた回だけ新着として再検知され、
 * (b) 同じ版を再度 poll しても二重ポップアップしない、の両方が同時に成り立つ。
 */
export function notificationSeenKey(n: NotificationSeenKeyInput): string {
  const id = n.notifId;
  const type = typeof n.type === 'string' ? n.type.trim() : '';
  if (!GROUP_REVISION_POPUP_TYPES.has(type)) return id;
  const rawCount = n.count;
  const count =
    typeof rawCount === 'number' && Number.isFinite(rawCount) && rawCount >= 1
      ? Math.floor(rawCount)
      : 1;
  const lastAt = typeof n.lastAt === 'string' ? n.lastAt.trim() : '';
  // 集約属性を持たない (集約導入前の) レコードは従来どおり notifId 単位で扱う。
  if (count <= 1 && !lastAt) return id;
  return `${id}#${count}@${lastAt}`;
}

/**
 * すでに認識済み (seen) のキー集合に含まれない通知だけを「新着」として返す。
 *
 * - notifId が空の要素は無視する
 * - 引数の seenKeys は変更しない (副作用なし)
 *
 * 初回ロード時に全キーを seen に積んでおくことで、既存通知を大量表示しない設計。
 * キーの決め方は {@link notificationSeenKey} を参照 (既定は notifId そのもの)。
 */
export function detectNewNotifications<T extends NotificationSeenKeyInput>(
  items: readonly T[],
  seenKeys: ReadonlySet<string>,
): T[] {
  return items.filter((n) => Boolean(n.notifId) && !seenKeys.has(notificationSeenKey(n)));
}

/** 1 件の通知をトースト 1 行のテキストに整形する (title / 本文はデータなので訳さない)。 */
export function formatNotificationLine(
  n: Pick<Notification, 'title' | 'messagePreview'>,
  locale: Locale = 'ja',
): string {
  const title = (n.title ?? '').trim() || translate(locale, 'notifications.popup.fallbackTitle');
  const preview = (n.messagePreview ?? '').trim();
  return preview ? `${title}: ${preview}` : title;
}

/**
 * 通知クリック時の遷移先パスを返す (QA-093: 通知から該当画面へワンクリック遷移)。
 *
 * 解決は共通 resolver (`notificationDestination`) に委譲する。旧実装は caseId だけを見て
 * `/cases/{caseId}?tab=message` へ固定遷移しており、NotificationDrawer と同じ
 * 「イベント種別を無視する」欠陥を二重に持っていた。以後は destination の契約を
 * resolver 側の 1 箇所だけで定義し、この関数は薄い委譲に留める。
 *
 * 解決できない場合は null (= クリック遷移なし)。副作用 (API 呼び出し) は行わない。
 */
export function notificationTargetPath(
  n: Pick<Notification, 'type'> &
    Partial<Pick<Notification, 'caseId' | 'collectionId' | 'clientId'>>,
): string | null {
  return notificationDestination(n);
}

/**
 * 新着メッセージとして構造化表示する通知種別
 * (notification-banner-readability-20260820)。
 *
 * **allowlist にした理由**: ここに無い種別 (書類アップロード / リマインド / 外部チャネル 等) は
 * 従来どおり 1 行表示のままにして、表示の変更範囲を「依頼者からの新着メッセージ」に閉じる。
 * `NotificationType` が増えても自動的に構造化対象へ入らない。
 */
const MESSAGE_POPUP_TYPES: ReadonlySet<string> = new Set([
  'portal.message_received',
  'client.message_received',
]);

/** 構造化ポップアップの行動導線ラベル (日本語)。遷移先自体は targetPath が担う。 */
export const MESSAGE_POPUP_ACTION_LABEL: string = notificationsJa.popup.messageAction;

/** ポップアップ 1 件分の表示内容。 */
export interface PopupItem {
  /**
   * トーストに表示するテキスト。
   * 構造化表示のときも従来互換の 1 行文字列として保持する
   * (構造化を解さない経路・集約表示の fallback に使う)。
   */
  message: string;
  /** クリックで遷移する先。null ならクリック遷移なし (従来挙動) */
  targetPath: string | null;
  /** 構造化表示の見出し。未設定なら従来の 1 行表示 */
  title?: string;
  /** 構造化表示の本文プレビュー。**切り詰めない** (行数は CSS の line-clamp が抑える) */
  preview?: string;
  /** 構造化表示の行動導線ラベル */
  actionLabel?: string;
}

/** 空文字・空白のみ・null/undefined を等しく「値なし」として扱う。 */
function nonEmpty(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 新着メッセージ通知の構造化表示 (見出し + プレビュー) を組み立てる。
 *
 * - 対象外の種別・`clientName` 欠落では `null` を返し、呼び出し側は従来の 1 行表示へ倒す
 *   (未マップ値 fallback 必須。CLAUDE.md §2.4)
 * - プレビューは `messagePreview` をそのまま渡す。**独自の文字数 truncate はしない** —
 *   表示上の制限は ToastContainer 側の line-clamp が担う
 */
export function buildMessagePopupContent(
  n: Pick<Notification, 'type'> & Partial<Pick<Notification, 'clientName' | 'messagePreview'>>,
  locale: Locale = 'ja',
): { title: string; preview?: string } | null {
  if (!MESSAGE_POPUP_TYPES.has(nonEmpty(n.type))) return null;
  const clientName = nonEmpty(n.clientName);
  if (!clientName) return null;
  const preview = nonEmpty(n.messagePreview);
  const title = translate(locale, 'notifications.popup.messageTitle', { client: clientName });
  return preview ? { title, preview } : { title };
}

/**
 * 新着通知群から、実際に表示するトースト (メッセージ + クリック遷移先) 配列を作る。
 *
 * - 0 件なら空配列 (表示しない)
 * - maxIndividual 件以下なら 1 件ずつ個別表示 (遷移先は通知種別ごとに resolver が決める)
 * - 超えた場合はトースト連発を避けて 1 件に集約 ("新着通知が N 件あります")。
 *   集約表示は単一リソースに紐付かないため遷移先なし
 */
export function buildPopupItems(
  fresh: ReadonlyArray<
    Pick<Notification, 'title' | 'messagePreview' | 'type'> &
      Partial<Pick<Notification, 'caseId' | 'collectionId' | 'clientId' | 'clientName'>>
  >,
  maxIndividual: number = MAX_INDIVIDUAL_POPUPS,
  locale: Locale = 'ja',
): PopupItem[] {
  if (fresh.length === 0) return [];
  if (fresh.length <= maxIndividual) {
    return fresh.map((n) => {
      const base: PopupItem = {
        message: formatNotificationLine(n, locale),
        targetPath: notificationTargetPath(n),
      };
      // 新着メッセージだけ構造化表示にする。対象外は base のまま = 従来の 1 行表示。
      const structured = buildMessagePopupContent(n, locale);
      if (!structured) return base;
      return {
        ...base,
        title: structured.title,
        ...(structured.preview ? { preview: structured.preview } : {}),
        actionLabel: translate(locale, 'notifications.popup.messageAction'),
      };
    });
  }
  // 集約表示は単一リソースに紐付かないため、遷移先も構造化表示も持たない (従来どおり)。
  return [
    {
      message: translate(locale, 'notifications.popup.aggregated', { count: fresh.length }),
      targetPath: null,
    },
  ];
}

/** poll 1 回分の「toast を出すか / 音を鳴らすか」の判定結果。 */
export interface PopupActionDecision {
  /** toast を表示するか (visible かつ inAppPopup=true のときのみ) */
  showToasts: boolean;
  /** 通知音を試行するか (inAppSound=true なら hidden でも鳴らす) */
  playSound: boolean;
}

/**
 * 新着検知後の表示/再生アクションを決める純粋関数 (通知改善 B)。
 *
 * - toast: visible かつ inAppPopup=true のときだけ出す。hidden 中に出しても表示時間内に
 *   見えず消えるだけなので出さない (hidden 中の未読確認は既存ベル / ドロワーに任せる)。
 * - sound: inAppSound=true なら visible / hidden を問わず試行する (別タブ中も音で気づく)。
 *   inAppPopup の値には依存しない (popup OFF + sound ON でも鳴る)。
 * - 新着 0 件なら何もしない。
 */
export function decidePopupActions(args: {
  freshCount: number;
  hidden: boolean;
  prefs: { inAppPopup: boolean; inAppSound: boolean };
}): PopupActionDecision {
  if (args.freshCount <= 0) return { showToasts: false, playSound: false };
  return {
    showToasts: !args.hidden && args.prefs.inAppPopup,
    playSound: args.prefs.inAppSound,
  };
}
